import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";
import { AlertTriangle, Ban } from "lucide-react";

/**
 * Sprint 10b — Billing-Stabilisierung: globaler Status-Banner.
 *
 * Sichtbar NUR im Mandanten-Kontext (nicht in /agency/*). Zeigt:
 *  - past_due → amber Warnung (Grace, App bleibt nutzbar)
 *  - cancelled → roter Block-Hinweis (produktive Aktionen blockiert)
 *  - active / legacy_grandfathered / leer → nichts
 */
export default function BillingStatusBanner() {
  const { company } = useAuth();
  const loc = useLocation();
  const inAgencyView = loc.pathname.startsWith("/agency");

  if (inAgencyView) return null;
  const bs = (company?.billing_status || "").trim();
  if (!bs || bs === "active" || bs === "legacy_grandfathered") return null;

  if (bs === "past_due") {
    return (
      <div
        data-testid="billing-banner-past-due"
        className="bg-amber-50 border-b border-amber-200 text-amber-900 px-4 md:px-8 py-2 text-sm flex items-center gap-2"
      >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="flex-1">
          <strong>Zahlungsproblem:</strong> Die letzte Stripe-Abbuchung ist fehlgeschlagen. Bitte
          Zahlungsmittel im Kunden-Portal aktualisieren — die App bleibt vorerst nutzbar.
          {company?.billing_last_error && (
            <span className="text-amber-700"> · {company.billing_last_error}</span>
          )}
        </span>
      </div>
    );
  }

  if (bs === "cancelled") {
    return (
      <div
        data-testid="billing-banner-cancelled"
        className="bg-red-50 border-b border-red-200 text-red-900 px-4 md:px-8 py-2 text-sm flex items-center gap-2"
      >
        <Ban className="w-4 h-4 shrink-0" />
        <span className="flex-1">
          <strong>Abo gekündigt:</strong> Produktive Aktionen sind aktuell blockiert (Versand, Konfigurator,
          automatische Nachfassung). Bitte ein neues Mandantenpaket über den Agentur-Bereich erwerben.
        </span>
      </div>
    );
  }

  if (bs === "checkout_pending") {
    return (
      <div
        data-testid="billing-banner-checkout-pending"
        className="bg-slate-50 border-b border-slate-200 text-slate-700 px-4 md:px-8 py-2 text-sm flex items-center gap-2"
      >
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="flex-1">
          Zahlung steht noch aus — Mandant ist erst nach erfolgreicher Stripe-Abrechnung produktiv.
        </span>
      </div>
    );
  }

  return null;
}
